import { AnalyticsService } from '../..'
import { Entity } from '../../../../domain/entity'
import { IEvent } from '../../../../domain/event/event'
import { DOMEvents } from '../../../../utils/dom'
import { IProfile } from '../../../common/profile'
import { IAnalyticsAdapter, IAnalyticsProviderConfig } from '../adapter'
import { MParticle } from '../mparticle'

import { AnalyticsEvent, IAnalyticsEvent } from '../../../../domain/analytics/event'

import { Default } from '../../../../domain/analytics/providers/default'
import { PageScroll } from '../../../../domain/analytics/providers/page-scroll'

import { SetAttributes } from '../../../../domain/analytics/providers/mparticle/events/user/set-attributes'

import { UserEvents } from '../../../../domain/analytics/providers/mparticle/user-events'
import { Visible } from '../../../../domain/analytics/providers/visible'

export interface IUserAttributeChangeEventDetail {
    action: 'set' | 'remove'
    key: string
    value?: string
}

export class EventAdapter extends Entity implements IAnalyticsAdapter<IAnalyticsProviderConfig> {
    public events: IEvent[]
    public signIn: boolean
    public signOut: boolean
    public mparticle = new MParticle()
    private meta: object
    private conf: object
    private processedElements: Element[] = []

    /**
     * initialize Event Adapter
     *
     */
    public initialize(
        creds: IAnalyticsProviderConfig,
        profile: IProfile,
        events: IAnalyticsEvent[],
        meta: object, conf: object, isDev: boolean): void {

        this.meta = meta
        this.conf = conf
        creds.isDev = isDev || creds.isDev

        this.mparticle.initialize(creds, profile)

        // bind events
        this.bindEvents(events)

        // bind custom events
        this.bindCustomEvent()

        // bind custom user attribute change events
        this.bindUserAttributeChangeEvent()
    }

    /**
     * Updates user attributes.
     */
    public update(profile: IProfile) {
        SetAttributes(profile)
    }

    /**
     * Get the correct event object for the registered event type
     *
     * @param type
     * @param value
     * @param action
     * @param element
     */
    public getEventByType(type: string, value: string, action: string, element?: Element): IEvent {

        if (action === DOMEvents.BEFORE_UNLOAD) {
            switch (type.toLocaleLowerCase()) {
                case AnalyticsService.scrollEventTypes.pageScroll:
                    return new PageScroll(type, value, action, element)
                default:
                    return new Default(type, value, action, element)
            }
        } else if(action === 'visible') {
            return new Visible(type, value, action, element)
        }
        return new Default(type, value, action, element)
    }

    /**
     * Bind click events to it elements
     * page-load evets are added to this.events and passed
     * over pageOnLoadEvents
     */
    public bindEvents(events: IAnalyticsEvent[]): void {
        const newEvents = events.filter((eventItem) => this.processedElements.indexOf(eventItem.element) === -1)
        this.processedElements = this.processedElements.concat(events.map((eventItem) => eventItem.element))
        for (const event of newEvents) {
            // Instanceof sub classes of `Default` class OR Instanceof of `PageScroll` class
            if (event instanceof Default || event instanceof PageScroll) {
                event.setMetaProperties(this.conf)
            } else if (event instanceof Visible) {
                event.setMetaProperties(this.conf)
                event.addEventListener()
            } else if (event instanceof AnalyticsEvent) {
                // if the event has been identified as an generic analytics Event specific one, set the meta properties
                // - possibly not the cleanest way to do this
                event.setMetaProperties(this.meta)
            }

            // if the event is a page load event, fire it right away
            if (AnalyticsService.ACTION_PAGE_LOAD === event.action) {
                // handle the event
                event.handle()

            } else if (event.action === DOMEvents.click) {
                // add a click listener for the event
                event.element.addEventListener(DOMEvents.click, (evt: UIEvent) => event.handle(evt))

            } else if (event.action === DOMEvents.scroll) {
                // add a scroll listener
                document.addEventListener(DOMEvents.scroll, event.handle.bind(event))

            } else if (event.action === DOMEvents.BEFORE_UNLOAD) {
                // add a pagehide listener
                // https://developer.apple.com/library/archive/documentation/AppleApplications/
                // Reference/SafariWebContent/HandlingEvents/HandlingEvents.html#//apple_ref/doc/
                // uid/TP40006511-SW5
                window.addEventListener(DOMEvents.PAGE_HIDE, event.handle.bind(event))
            } else if (event.action === DOMEvents.FOCUS_IN) {
                // add a focusin listener for handling form first interaction events
                event.element.addEventListener(DOMEvents.FOCUS_IN, event.handle.bind(event), { once: true })
            }
        }
    }

    /**
     * Bind custom events to document
     */
    private bindCustomEvent(): void {
        document.addEventListener(
            DOMEvents.CUSTOM_EVENT,
            (e: CustomEvent) => {
                if (e.detail.event_name) {
                    const {event_name, ...eventConfig} = e.detail
                    const defaultEvent = new Default(event_name, JSON.stringify(eventConfig), '')
                    defaultEvent.setMetaProperties(this.conf)
                    defaultEvent.handle()
                } else {
                    console.warn('Analytics Adapter: missing event_name in the custom event')
                }
            },
            false
        )
    }

    /**
     * Bind custom user attribute change events to document
     */
    private bindUserAttributeChangeEvent(): void {
        document.addEventListener(
            DOMEvents.USER_ATTRIBUTE_CHANGE,
            (e: CustomEvent) => {
                if (e.detail.action) {
                    const {action, key, value} = e.detail as IUserAttributeChangeEventDetail
                    const userEvent = new UserEvents()
                    if (action === 'set') {
                        userEvent.setUserAttribute(key, value)
                    } else {
                        // for future implementation: eg: `remove`
                    }
                } else {
                    console.warn('Analytics Adapter: missing key in the custom user attribute change event')
                }
            },
            false
        )
    }
}
